import './style.scss'
import React, { useMemo, useEffect } from 'react'
import { AsYouType, isValidPhoneNumber, getCountryCallingCode, type CountryCode } from 'libphonenumber-js'
import { Text, tokens, UIProps } from 'src/componentv2/Base/ui'

export interface Country {
    code: CountryCode
    label?: string
}

export interface PhoneFieldProps extends UIProps {
    label?: React.ReactNode
    helperText?: React.ReactNode
    /**
     * Full phone number INCLUDING the leading country code, e.g. "+2348031234567".
     * This is the exact string sent to the backend — country is a frontend-only concern.
     */
    value?: string
    onChange?: (fullValue: string) => void
    country?: CountryCode
    onCountryChange?: (code: CountryCode) => void
    countries?: Country[]
    error?: React.ReactNode
    disabled?: boolean
    /** fires whenever the entered number's validity (for the selected country) changes */
    onValidityChange?: (isValid: boolean) => void
}

const DEFAULT_COUNTRIES: Country[] = [
    { code: 'NG' },
    { code: 'GH' },
    { code: 'KE' },
    { code: 'ZA' },
    { code: 'US' },
    { code: 'GB' },
    { code: 'FR' },
]

const onlyDigits = (raw: string) => raw.replace(/\D/g, '')

export const PhoneField: React.FC<PhoneFieldProps> = ({
                                                          label, helperText, value = '', onChange, country = 'NG', onCountryChange,
                                                          countries = DEFAULT_COUNTRIES,
                                                          error, disabled, width = '100%', className = '',
                                                          onValidityChange,
                                                      }) => {
    const dialCode = `+${getCountryCallingCode(country)}`

    // The dial code is fixed/derived from `country` — strip it off `value` to get
    // just the part the user is allowed to type/edit.
    const nationalDigits = useMemo(() => {
        const digitsOnly = onlyDigits(value)
        const dialDigits = onlyDigits(dialCode)
        return digitsOnly.startsWith(dialDigits) ? digitsOnly.slice(dialDigits.length) : digitsOnly
    }, [value, dialCode])

    const formattedNational = useMemo(() => {
        if (!nationalDigits) return ''
        return new AsYouType(country).input(nationalDigits)
    }, [nationalDigits, country])

    const fullNumber = `${dialCode}${nationalDigits}`

    const isValid = useMemo(() => {
        if (!nationalDigits) return true // emptiness is handled by `required`, not here
        return isValidPhoneNumber(fullNumber, country)
    }, [fullNumber, nationalDigits, country])

    useEffect(() => {
        onValidityChange?.(isValid)
    }, [isValid, onValidityChange])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const digits = onlyDigits(e.target.value) // strips letters/symbols — digits only
        onChange?.(`${dialCode}${digits}`)
    }

    const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newCountry = e.target.value as CountryCode
        const newDial = `+${getCountryCallingCode(newCountry)}`
        onCountryChange?.(newCountry)
        // country code swaps, but whatever the user already typed carries over as-is
        onChange?.(`${newDial}${nationalDigits}`)
    }

    return (
        <div className={`phone ${error ? 'phone--error' : ''} ${className}`} style={{ width }}>
            {label && <label className="phone__label">{label}</label>}

            <div className="phone__control" style={{ display: 'flex', alignItems: 'center' }}>
                <select
                    className="phone__country"
                    value={country}
                    disabled={disabled}
                    onChange={handleCountryChange}
                    style={{ alignSelf: 'stretch' }}
                >
                    {countries.map((c) => (
                        <option key={c.code} value={c.code}>
                            {c.code}
                        </option>
                    ))}
                </select>

                {/* fixed, non-editable — the user never types or deletes the country code itself */}
                <span
                    className="phone__dial-code"
                    style={{ display: 'inline-flex', alignItems: 'center', alignSelf: 'stretch', marginLeft: '0.8rem' }}
                >
                    {dialCode}
                </span>

                <input
                    className="phone__input"
                    type="tel"
                    inputMode="numeric"
                    value={formattedNational}
                    disabled={disabled}
                    onChange={handleInputChange}
                    maxLength={20}
                    style={{ alignSelf: 'stretch' }}
                />
            </div>

            {(error || helperText || (nationalDigits && !isValid)) && (
                <Text
                    className="phone__helper"
                    fontSize="1.3"
                    topMargin="0.6"
                    color={error || (nationalDigits && !isValid) ? '#D92D20' : tokens.textMuted}
                >
                    {error || (nationalDigits && !isValid ? 'Enter a valid phone number for the selected country' : helperText)}
                </Text>
            )}
        </div>
    )
}

export default PhoneField